import { CLASSES } from "../content/core";
import { ITEMS } from "../content/items";
import { ENEMIES, PETS } from "../content/world";
import { applyAction, gainPetXpMutable, gainSkillMutable } from "../game/engine";
import { pick, randomInt, stream } from "../game/rng";
import type { ClassId, GameState, MineBlock, MineEnemyState, MineProjectile, MineState, SkillId } from "../game/types";
import { rollLoot } from "./loot";

const clone = <T,>(value: T): T => structuredClone(value);
const TIER_ORES: MineBlock["type"][][] = [
  ["stone", "dirt", "copper", "coal"],
  ["stone", "iron", "silver", "copper"],
  ["stone", "gold", "emberglass", "iron"],
  ["stone", "moonstone", "silver", "gold"],
  ["stone", "starsteel", "moonstone", "gold"],
];
const TIER_ENEMIES = [
  ["root-mite", "barkling", "spore-bat", "copper-scarab"],
  ["iron-grub", "rail-wraith", "oreback", "silver-seer"],
  ["cinder-crawler", "magma-moth", "glass-spitter", "slag-knight"],
  ["archive-leech", "moon-jelly", "drowned-scholar", "mirror-guard"],
  ["crown-husk", "season-wisp", "loom-sentinel", "hour-knight"],
];
const TIER_BOSSES = ["rootwarden", "bellows-king", "caldera-heart", "index-leviathan", "crown-loom"];
const BLOCK_HP: Record<MineBlock["type"], number> = { dirt: 10, stone: 18, coal: 20, copper: 22, iron: 34, silver: 40, gold: 52, emberglass: 60, moonstone: 75, starsteel: 100, platform: 999, hazard: 999 };
const BLOCK_DROP: Partial<Record<MineBlock["type"], string>> = { stone: "stone", coal: "coal", copper: "copper-ore", iron: "iron-ore", silver: "silver-ore", gold: "gold-ore", emberglass: "emberglass", moonstone: "moonstone", starsteel: "starsteel" };

function equipmentStat(state: GameState, stat: keyof NonNullable<(typeof ITEMS)[string]["stats"]>): number {
  return Object.values(state.equipment).reduce((sum, stack) => sum + (stack ? ITEMS[stack.itemId]?.stats?.[stat] ?? 0 : 0), 0);
}

function spawnEnemy(state: GameState, enemyId: string, x: number, y: number, index: number): MineEnemyState {
  const def = ENEMIES[enemyId];
  const difficulty = state.settings.difficulty === "story" ? .82 : state.settings.difficulty === "frontier" ? 1.18 : 1;
  return { uid: `${enemyId}-${state.seed}-${state.day}-${index}`, enemyId, x, y, vx: 0, vy: 0, hp: Math.round(def.health * difficulty), maxHp: Math.round(def.health * difficulty), direction: index % 2 ? 1 : -1, cooldown: index * .2, hurt: 0, alive: true };
}

export function generateMine(state: GameState, tier: number, depth = 1): MineState {
  const safeTier = Math.max(1, Math.min(5, tier));
  const width = 56;
  const height = 26;
  const rng = stream(state.seed, `mine-${safeTier}-${depth}-${state.day}`);
  const blocks: MineBlock[] = [];
  const oreTypes = TIER_ORES[safeTier - 1];
  let blockIndex = 0;
  for (let y = height - 5; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const roll = rng();
      const type = y === height - 1 ? "stone" : roll < .62 ? "stone" : roll < .77 ? oreTypes[1] : roll < .9 ? oreTypes[2] : oreTypes[3];
      const hp = BLOCK_HP[type];
      blocks.push({ id: `b-${blockIndex++}`, x, y, type, hp, maxHp: hp, solid: true });
    }
  }
  for (let segment = 0; segment < 8; segment += 1) {
    const startX = 5 + segment * 6 + randomInt(-1, 1, rng);
    const platformY = height - 8 - (segment % 3) * 3;
    for (let x = startX; x < Math.min(width - 3, startX + randomInt(3, 6, rng)); x += 1) blocks.push({ id: `b-${blockIndex++}`, x, y: platformY, type: "platform", hp: 999, maxHp: 999, solid: true });
    if (rng() < .75) {
      const ore = pick(oreTypes.slice(1), rng);
      const hp = BLOCK_HP[ore];
      blocks.push({ id: `b-${blockIndex++}`, x: startX + 1, y: platformY - 1, type: ore, hp, maxHp: hp, solid: true });
    }
  }

  const enemies: MineEnemyState[] = [];
  const pool = TIER_ENEMIES[safeTier - 1];
  const count = 5 + safeTier + Math.floor(depth / 3);
  for (let index = 0; index < count; index += 1) enemies.push(spawnEnemy(state, pick(pool, rng), 8 + index * 5 + rng() * 2, height - 6.5, index));
  const bossFloor = depth % 5 === 0;
  if (bossFloor) enemies.push(spawnEnemy(state, TIER_BOSSES[safeTier - 1], width - 10, height - 6.5, 99));

  return {
    active: true,
    tier: safeTier,
    depth,
    width,
    height,
    blocks,
    enemies,
    projectiles: [],
    player: { x: 2.5, y: height - 6.5, vx: 0, vy: 0, grounded: true, coyote: .12, jumpHeld: false, facing: 1, attackCooldown: 0, dashCooldown: 0, invulnerable: 0 },
    exitX: width - 2.5,
    exitY: height - 6.5,
    mined: 0,
    kills: 0,
    bossDefeated: !bossFloor,
    message: bossFloor ? "A sovereign presence blocks the deeper passage." : `Depth ${depth}: find the far lift or keep mining.`,
  };
}

export function enterMine(input: GameState, tier = 1): GameState {
  const state = clone(input);
  const maxTier = Math.max(1, Math.min(5, 1 + Math.floor(Object.entries(state.quests).filter(([id, quest]) => id.startsWith("main-") && quest.status === "complete").length / 18)));
  const safeTier = Math.min(tier, maxTier);
  state.mine = generateMine(state, safeTier, 1);
  state.scene = "mine";
  state.panel = null;
  state.message = `Entered mine tier ${safeTier}. A/D move, Space jumps, J attacks, K mines, Shift dashes.`;
  return state;
}

export interface MineControls { left: boolean; right: boolean; jump: boolean; dash: boolean }

function nearestBlock(mine: MineState, x: number, y: number, range: number): MineBlock | undefined {
  return mine.blocks.filter((block) => block.solid && block.type !== "platform" && Math.hypot(block.x + .5 - x, block.y + .5 - y) <= range).sort((a, b) => Math.hypot(a.x - x, a.y - y) - Math.hypot(b.x - x, b.y - y))[0];
}

function collides(mine: MineState, x: number, y: number): boolean {
  return mine.blocks.some((block) => block.solid && x + .32 > block.x && x - .32 < block.x + 1 && y + .72 > block.y && y - .72 < block.y + 1);
}

function groundY(mine: MineState): number { return mine.height - 5.72; }

export function stepMine(input: GameState, controls: MineControls, dt: number): GameState {
  const state = clone(input);
  const mine = state.mine;
  if (!mine || !mine.active) return state;
  const player = mine.player;
  const speed = 5.2 * state.stats.speed * (state.classId === "shadeweaver" ? 1.08 : 1);
  const desired = (controls.right ? 1 : 0) - (controls.left ? 1 : 0);
  player.vx += (desired * speed - player.vx) * Math.min(1, dt * 14);
  if (desired) player.facing = Math.sign(desired);
  player.grounded = player.y >= groundY(mine) - .03;
  player.coyote = player.grounded ? .12 : Math.max(0, player.coyote - dt);
  if (controls.jump && player.coyote > 0 && !player.jumpHeld) { player.vy = -8.8; player.coyote = 0; player.jumpHeld = true; }
  if (!controls.jump) player.jumpHeld = false;
  if (controls.dash && player.dashCooldown <= 0 && state.stats.stamina >= 7) { player.vx = player.facing * 12; player.dashCooldown = state.classId === "shadeweaver" ? .55 : .9; player.invulnerable = .22; state.stats.stamina -= 7; }
  player.vy += 24 * dt;
  player.attackCooldown = Math.max(0, player.attackCooldown - dt);
  player.dashCooldown = Math.max(0, player.dashCooldown - dt);
  player.invulnerable = Math.max(0, player.invulnerable - dt);

  let nextX = Math.max(.4, Math.min(mine.width - .4, player.x + player.vx * dt));
  if (!collides(mine, nextX, player.y)) player.x = nextX; else player.vx = 0;
  let nextY = player.y + player.vy * dt;
  if (nextY >= groundY(mine)) { nextY = groundY(mine); player.vy = 0; player.grounded = true; }
  if (!collides(mine, player.x, nextY)) player.y = nextY; else { if (player.vy > 0) player.grounded = true; player.vy = 0; }

  const difficultyDamage = state.settings.difficulty === "story" ? .72 : state.settings.difficulty === "frontier" ? 1.25 : 1;
  for (const enemy of mine.enemies) {
    if (!enemy.alive) continue;
    const def = ENEMIES[enemy.enemyId];
    enemy.cooldown = Math.max(0, enemy.cooldown - dt);
    enemy.hurt = Math.max(0, enemy.hurt - dt);
    const dx = player.x - enemy.x;
    const distance = Math.abs(dx);
    if (def.behavior === "flying") enemy.y += Math.sin(performance.now() / 500 + enemy.x) * dt * .5;
    if (def.behavior === "ranged" && distance < 10 && enemy.cooldown <= 0) {
      const direction = Math.sign(dx) || 1;
      mine.projectiles.push({ uid: `ep-${state.rngCounter++}`, x: enemy.x, y: enemy.y, vx: direction * 5.5, vy: 0, damage: Math.round(def.damage * difficultyDamage), friendly: false, life: 2.5, color: def.palette[1] });
      enemy.cooldown = 1.6;
    } else if (def.behavior !== "ranged" || distance > 5) {
      const pace = def.speed * (def.behavior === "charger" && distance < 5 ? 3 : 1.3);
      enemy.x += Math.sign(dx) * pace * dt;
    }
    if (distance < .9 + def.size * .35 && Math.abs(player.y - enemy.y) < 1.2 && enemy.cooldown <= 0 && player.invulnerable <= 0) {
      const reduction = state.stats.armor / (state.stats.armor + 80 + mine.tier * 25);
      const damage = Math.max(1, Math.floor(def.damage * (def.boss ? .78 : 1) * difficultyDamage * (1 - reduction)));
      state.stats.health -= damage;
      player.invulnerable = .65;
      player.vx = -Math.sign(dx) * 6;
      enemy.cooldown = def.boss ? 1.45 : 1;
      mine.message = `${def.name} hits for ${damage}.`;
    }
  }

  for (const projectile of mine.projectiles) {
    projectile.x += projectile.vx * dt;
    projectile.y += projectile.vy * dt;
    projectile.life -= dt;
    if (!projectile.friendly && Math.abs(projectile.x - player.x) < .45 && Math.abs(projectile.y - player.y) < .8 && player.invulnerable <= 0) {
      const reduction = state.stats.armor / (state.stats.armor + 80 + mine.tier * 25);
      state.stats.health -= Math.max(1, Math.floor(projectile.damage * (1 - reduction)));
      player.invulnerable = .55;
      projectile.life = 0;
    }
    if (projectile.friendly) {
      const target = mine.enemies.find((enemy) => enemy.alive && Math.abs(enemy.x - projectile.x) < .55 && Math.abs(enemy.y - projectile.y) < .8);
      if (target) { target.hp -= projectile.damage; target.hurt = .2; projectile.life = 0; }
    }
  }
  mine.projectiles = mine.projectiles.filter((projectile) => projectile.life > 0 && projectile.x > 0 && projectile.x < mine.width);

  const newlyDead = mine.enemies.filter((enemy) => enemy.alive && enemy.hp <= 0);
  for (const enemy of newlyDead) {
    enemy.alive = false;
    mine.kills += 1;
    state.gameStats.enemiesDefeated += 1;
    const def = ENEMIES[enemy.enemyId];
    const loot = rollLoot(state, def.lootTableId, enemy.uid);
    Object.assign(state, loot.state);
    const combatSkill: SkillId = state.classId === "arcanist" ? "arcana" : state.classId === "ranger" ? "ranged" : "melee";
    gainSkillMutable(state, combatSkill, def.xp);
    gainPetXpMutable(state, Math.ceil(def.xp / 3), combatSkill);
    if (def.boss) { mine.bossDefeated = true; state.gameStats.bossesDefeated += 1; }
    const actionType = def.boss ? "boss" : "defeat";
    const updated = applyAction(state, { type: actionType, target: enemy.enemyId, amount: 1, skill: combatSkill, xp: 0 });
    Object.assign(state, updated);
    mine.message = `${def.name} defeated${loot.drops.length ? `: ${loot.drops.map((drop) => `${drop.quantity} ${ITEMS[drop.itemId].name}`).join(", ")}` : ""}.`;
  }

  if (state.stats.health <= 0) {
    state.stats.health = Math.max(1, Math.floor(state.stats.maxHealth * .35));
    state.crowns = Math.max(0, state.crowns - Math.ceil(state.crowns * .08));
    state.scene = "world";
    state.mine = undefined;
    state.location = "lantern-acre";
    state.message = "You wake at Lantern Acre. Some crowns paid for the rescue.";
    return state;
  }
  return state;
}

function weaponPower(state: GameState): number {
  const weapon = state.equipment.weapon;
  const base = weapon ? ITEMS[weapon.itemId]?.stats?.damage ?? 8 : 8;
  const classBonus = state.classId === "warden" ? 1.08 : state.classId === "shadeweaver" ? 1.05 : 1;
  return Math.max(1, Math.floor(base * classBonus));
}

export function mineAttack(input: GameState, mode: "attack" | "special" = "attack"): GameState {
  const state = clone(input);
  const mine = state.mine;
  if (!mine || mine.player.attackCooldown > 0) return state;
  const player = mine.player;
  const cls = CLASSES[state.classId];
  const skill: "melee" | "ranged" | "arcana" = state.classId === "arcanist" ? "arcana" : state.classId === "ranger" ? "ranged" : "melee";
  const skillScale = 1 + state.skills[skill].level * .012;
  let damage = Math.floor(weaponPower(state) * skillScale);
  const rng = stream(state.seed, `attack-${state.day}-${state.rngCounter++}`);
  const critChance = .05 + equipmentStat(state, "crit") / 100 + (state.classId === "shadeweaver" ? .08 : 0);
  if (rng() < critChance) damage = Math.floor(damage * 1.65);

  if (mode === "special") {
    const usesMana = state.classId === "arcanist" || state.classId === "greenbinder";
    const cost = state.classId === "arcanist" ? 12 : state.classId === "greenbinder" ? 14 : 10;
    const pool = usesMana ? "mana" : "stamina";
    if (state.stats[pool] < cost) {
      mine.message = `Not enough ${pool} for ${cls.technique}.`;
      return state;
    }
    state.stats[pool] -= cost;

    if (state.classId === "arcanist") {
      const boltDamage = Math.floor(damage * 1.15);
      for (const offset of [-.22, 0, .22]) mine.projectiles.push({ uid: `pp-${state.rngCounter++}`, x: player.x + player.facing * .5, y: player.y - .15 + offset, vx: player.facing * 8.5, vy: offset * 1.2, damage: boltDamage, friendly: true, life: 2.5, color: cls.palette[1] });
      mine.message = `${cls.technique}: three prism bolts fan through the chamber.`;
    } else if (state.classId === "ranger") {
      const arrowDamage = Math.floor(damage * .88);
      for (const offset of [-.16, 0, .16]) mine.projectiles.push({ uid: `pp-${state.rngCounter++}`, x: player.x + player.facing * .5, y: player.y - .15 + offset, vx: player.facing * 11, vy: offset * 1.5, damage: arrowDamage, friendly: true, life: 2.4, color: cls.palette[1] });
      mine.message = `${cls.technique}: a three-arrow volley crosses the room.`;
    } else if (state.classId === "warden") {
      const targets = mine.enemies.filter((enemy) => enemy.alive && Math.abs(enemy.x - player.x) < 2.3 && Math.abs(enemy.y - player.y) < 1.7);
      for (const target of targets.slice(0, 4)) { target.hp -= Math.floor(damage * 1.85); target.hurt = .28; target.vx = player.facing * 5; target.cooldown = Math.max(target.cooldown, .8); }
      player.invulnerable = Math.max(player.invulnerable, .5);
      mine.message = `${cls.technique}: a guarded sweep breaks the enemy line.`;
    } else if (state.classId === "tinker") {
      const targets = mine.enemies.filter((enemy) => enemy.alive && Math.hypot(enemy.x - player.x, enemy.y - player.y) < 3.4);
      for (const target of targets) { target.hp -= Math.floor(damage * 1.75); target.hurt = .35; target.cooldown = Math.max(target.cooldown, 1.35); }
      mine.message = `${cls.technique}: a shock charge stuns nearby enemies.`;
    } else if (state.classId === "greenbinder") {
      const targets = mine.enemies.filter((enemy) => enemy.alive && Math.hypot(enemy.x - player.x, enemy.y - player.y) < 2.8);
      for (const target of targets) { target.hp -= Math.floor(damage * 1.45); target.hurt = .3; target.cooldown = Math.max(target.cooldown, 1.4); }
      state.stats.health = Math.min(state.stats.maxHealth, state.stats.health + Math.ceil(state.stats.maxHealth * .18));
      mine.message = `${cls.technique}: roots bind foes and restore your health.`;
    } else {
      player.x = Math.max(.5, Math.min(mine.width - .5, player.x + player.facing * 2.2));
      const targets = mine.enemies.filter((enemy) => enemy.alive && Math.abs(enemy.x - player.x) < 2.1 && Math.abs(enemy.y - player.y) < 1.6);
      for (const target of targets.slice(0, 3)) { target.hp -= Math.floor(damage * 2.15); target.hurt = .3; }
      player.invulnerable = Math.max(player.invulnerable, .48);
      mine.message = `${cls.technique}: you cut through the target's shadow.`;
    }
    player.attackCooldown = .78;
    gainSkillMutable(state, skill, 3);
    return state;
  }

  const ranged = state.classId === "ranger" || state.classId === "arcanist";
  if (ranged) {
    mine.projectiles.push({ uid: `pp-${state.rngCounter++}`, x: player.x + player.facing * .5, y: player.y - .15, vx: player.facing * (state.classId === "arcanist" ? 8 : 10), vy: 0, damage, friendly: true, life: 2.2, color: cls.palette[1] });
  } else {
    const targets = mine.enemies.filter((enemy) => enemy.alive && Math.abs(enemy.x - player.x) < 1.5 && Math.abs(enemy.y - player.y) < 1.4 && Math.sign(enemy.x - player.x) === player.facing);
    for (const target of targets.slice(0, 2)) { target.hp -= damage; target.hurt = .2; target.vx = player.facing * 3; }
  }
  player.attackCooldown = .32;
  gainSkillMutable(state, skill, 1);
  return state;
}

export function mineBlock(input: GameState): GameState {
  const state = clone(input);
  const mine = state.mine;
  if (!mine) return state;
  const block = nearestBlock(mine, mine.player.x + mine.player.facing * .75, mine.player.y, 1.65);
  if (!block || state.stats.stamina < 2) { mine.message = block ? "Too tired to swing the pick." : "No breakable block is in reach."; return state; }
  const miningPower = 12 + state.skills.mining.level * 2 + equipmentStat(state, "mining") + (state.classId === "tinker" ? 8 : 0);
  const requiredTier = { stone: 0, dirt: 0, coal: 10, copper: 12, iron: 24, silver: 28, gold: 42, emberglass: 46, moonstone: 62, starsteel: 78, platform: 999, hazard: 999 }[block.type];
  if (miningPower < requiredTier) { mine.message = `Your mining power (${miningPower}) cannot break ${block.type}.`; return state; }
  state.stats.stamina -= Math.max(1, 3 - state.skills.mining.level * .05);
  block.hp -= miningPower;
  if (block.hp <= 0) {
    block.solid = false;
    mine.mined += 1;
    state.gameStats.blocksMined += 1;
    const dropId = BLOCK_DROP[block.type];
    if (dropId) {
      const existing = state.inventory.find((stack) => stack.itemId === dropId && !stack.rarity);
      if (existing) existing.quantity += 1; else state.inventory.push({ itemId: dropId, quantity: 1 });
      const updated = applyAction(state, { type: "mine", target: block.type === "stone" ? "stone" : dropId, amount: 1, skill: "mining", xp: 4 + mine.tier * 3 });
      Object.assign(state, updated);
    }
    gainPetXpMutable(state, 3 + mine.tier, "mining");
    mine.message = `Mined ${ITEMS[dropId ?? "stone"]?.name ?? block.type}.`;
  } else mine.message = `${block.type}: ${Math.ceil(block.hp)} integrity remains.`;
  return state;
}

export function descendMine(input: GameState): GameState {
  const state = clone(input);
  const mine = state.mine;
  if (!mine || Math.abs(mine.player.x - mine.exitX) > 2 || !mine.bossDefeated) { if (mine) mine.message = mine.bossDefeated ? "Reach the lift at the far right." : "The sovereign must be defeated first."; return state; }
  const nextDepth = mine.depth + 1;
  state.mine = generateMine(state, mine.tier, nextDepth);
  state.message = `Descended to depth ${nextDepth}.`;
  return applyAction(state, { type: "explore", target: `${["rootway", "deepiron", "ember", "archive", "crown"][mine.tier - 1]}-depth-${nextDepth}`, amount: 1, skill: "mining", xp: 15 + mine.tier * 5 });
}

export function leaveMine(input: GameState): GameState {
  const state = clone(input);
  state.scene = "world";
  state.mine = undefined;
  state.location = "lantern-acre";
  state.position = { x: 17, y: 17, direction: "down", moving: false, frame: 0 };
  state.minute += 90;
  state.message = "Returned to Lantern Acre with the expedition's supplies.";
  return state;
}
